import { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Plus,
  ArrowLeft,
  Trash2,
  Pencil,
  Search,
  ChevronLeft,
  ChevronRight,
  Save,
  AlertCircle,
  Globe,
  Loader2,
} from "lucide-react";
import { useToast } from "../components/ui/use-toast";
import {
  server_post_data,
  save_update_seo,
  delete_website_master_data,
  get_all_seo,
  Website_URL,
} from "../ServiceConnection/serviceconnection.js";

// ─── Helpers ──────────────────────────────────────────────────────────────────
const formatDate = (dateStr) =>
  new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

const emptyForm = {
  title_name: "",
  call_function_name: "",
  pretty_function_name: "",
  favicon: "",
  image_link: "",
  description: "",
  keywords: "",
  priority_screen: "0.8",
  changefreq: "weekly",
};

// ─── Delete Modal ─────────────────────────────────────────────────────────────
const DeleteModal = ({ seo, onConfirm, onClose, deleteLoader }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
    <div
      className="absolute inset-0 bg-black/40 backdrop-blur-sm"
      onClick={onClose}
    />
    <div className="relative z-10 w-full max-w-sm rounded-2xl border border-border bg-card shadow-2xl animate-fade-in">
      <div className="p-6 text-center">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-destructive/10">
          <Trash2 className="h-6 w-6 text-destructive" />
        </div>
        <h3 className="mb-2 text-base font-bold text-foreground">
          Delete SEO Entry?
        </h3>
        <p className="text-sm text-muted-foreground">
          Are you sure you want to delete{" "}
          <span className="font-semibold text-foreground">
            "{seo?.title_name}"
          </span>
          ? This cannot be undone.
        </p>
      </div>
      <div className="flex gap-3 border-t border-border p-4">
        <Button
          variant="outline"
          className="flex-1 rounded-xl"
          onClick={onClose}
          disabled={deleteLoader}
        >
          No
        </Button>
        <Button
          className="flex-1 gap-2 rounded-xl bg-destructive text-white hover:bg-destructive/90"
          onClick={onConfirm}
          disabled={deleteLoader}
        >
          {deleteLoader ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Trash2 className="h-4 w-4" />
          )}
          Yes, Delete
        </Button>
      </div>
    </div>
  </div>
);

// ─── SEO Table Row ────────────────────────────────────────────────────────────
const SeoTableRow = ({ seo, onEdit, onDelete }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <tr className="border-b border-border transition-colors hover:bg-muted/30">
      <td className="px-6 py-4">
        <p className="whitespace-nowrap text-xs text-muted-foreground">
          {formatDate(seo.entry_date)}
        </p>
      </td>
      <td className="max-w-[200px] px-6 py-4">
        <p className="truncate text-sm font-semibold text-foreground">
          {seo.title_name}
        </p>
        <p className="mt-0.5 truncate text-xs text-muted-foreground">
          {seo.call_function_name}
        </p>
      </td>
      <td className="hidden max-w-[220px] px-6 py-4 md:table-cell">
        <p
          className={`text-xs leading-relaxed text-muted-foreground ${expanded ? "" : "line-clamp-2"}`}
        >
          {seo.description}
        </p>
        {seo.description?.length > 80 && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="mt-1 text-[11px] font-medium text-primary hover:underline"
          >
            {expanded ? "Show less" : "Show more"}
          </button>
        )}
      </td>
      <td className="hidden max-w-[160px] px-6 py-4 lg:table-cell">
        <p className="truncate text-xs text-muted-foreground">{seo.keywords}</p>
      </td>
      <td className="hidden px-6 py-4 sm:table-cell">
        <span className="rounded-full bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary">
          {seo.priority_screen}
        </span>
      </td>
      <td className="px-6 py-4">
        <div className="flex items-center justify-end gap-1">
          <Button
            variant="ghost"
            size="icon-sm"
            title="Edit"
            onClick={() => onEdit(seo)}
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          {/* <Button
            variant="ghost"
            size="icon-sm"
            title="Delete"
            className="text-destructive hover:bg-destructive/10 hover:text-destructive"
            onClick={() => onDelete(seo)}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button> */}
        </div>
      </td>
    </tr>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────
const SeoManager = () => {
  const { toast } = useToast();

  const [seoList, setSeoList] = useState([]);
  const [fetchLoader, setFetchLoader] = useState(false);
  const [saveLoader, setSaveLoader] = useState(false);
  const [deleteLoader, setDeleteLoader] = useState(false);
  const [formVisible, setFormVisible] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editingId, setEditingId] = useState("0");
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  // ── GET ───────────────────────────────────────────────────────────────────
  const master_data_get = useCallback(
    async (start_date, end_date, flag, call_id) => {
      try {
        setFetchLoader(true);
        const fd = new FormData();
        fd.append("start_date", start_date);
        fd.append("end_date", end_date);
        fd.append("flag", flag);
        fd.append("call_id", call_id);
        const Response = await server_post_data(get_all_seo, fd);
        if (!Response.data.error) {
          if (Response.data.message.data_seo.length > 0) {
            setSeoList(Response.data.message.data_seo);
            if (flag === "3") {
              const data = Response.data.message.data_seo[0];
              setEditingId(data.primary_id);
              setForm({
                title_name: data.title_name || "",
                call_function_name: data.call_function_name || "",
                pretty_function_name: data.pretty_function_name || "",
                favicon: data.favicon || "",
                image_link: data.image || "",
                description: data.description || "",
                keywords: data.keywords || "",
                priority_screen: data.priority_screen || "0.8",
                changefreq: data.changefreq || "weekly",
              });
              setFormVisible(true);
            }
          } else {
            if (flag === "1") setSeoList([]);
          }
        } else {
          toast({
            title: "Error",
            description: Response.data.message,
            variant: "destructive",
          });
        }
      } catch (error) {
        console.error(error);
        toast({
          title: "Error",
          description: "Network error. Please try again.",
          variant: "destructive",
        });
      } finally {
        setFetchLoader(false);
      }
    },
    [],
  );

  useEffect(() => {
    master_data_get("", "", "1", "0");
  }, [master_data_get]);

  // ── DELETE ────────────────────────────────────────────────────────────────
  const master_data_action_update = async (call_id, for_status_final) => {
    try {
      setDeleteLoader(true);
      const fd = new FormData();
      fd.append("id_for_delete", call_id);
      fd.append("flag_for", "2");
      fd.append("for_status_final", for_status_final);
      const Response = await server_post_data(delete_website_master_data, fd);
      if (!Response.data.error) {
        toast({ title: "Deleted", description: Response.data.message });
        setDeleteTarget(null);
        master_data_get("", "", "1", "0");
      } else {
        toast({
          title: "Error",
          description: Response.data.message,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error(error);
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setDeleteLoader(false);
    }
  };

  // ── SAVE ──────────────────────────────────────────────────────────────────
  const handleSaveChangesdynamic = async () => {
    if (!validate()) return;
    try {
      setSaveLoader(true);
      const fd = new FormData();
      fd.append("main_id", editingId);
      fd.append("website_url", Website_URL);
      fd.append("title_name", form.title_name);
      fd.append("call_function_name", form.call_function_name);
      fd.append("pretty_function_name", form.pretty_function_name);
      fd.append("favicon", form.favicon);
      fd.append("image_link", form.image_link);
      fd.append("description", form.description);
      fd.append("keywords", form.keywords);
      fd.append("priority_screen", form.priority_screen);
      fd.append("changefreq", form.changefreq);
      const Response = await server_post_data(save_update_seo, fd);
      if (!Response.data.error) {
        toast({ title: "Success", description: Response.data.message });
        master_data_get("", "", "1", "0");
        resetForm();
        setFormVisible(false);
      } else {
        toast({
          title: "Error",
          description: Response.data.message,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error(error);
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSaveLoader(false);
    }
  };

  // ── Edit ──────────────────────────────────────────────────────────────────
  const handleEdit = (seo) => {
    master_data_get("", "", "3", seo.primary_id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId("0");
    setErrors({});
  };

  const validate = () => {
    const e = {};
    if (!form.title_name.trim()) e.title_name = "Title is required";
    if (!form.favicon.trim()) e.favicon = "Favicon link is required";
    if (!form.image_link.trim()) e.image_link = "Page image link is required";
    if (!form.description.trim()) e.description = "Description is required";
    if (!form.keywords.trim()) e.keywords = "Keywords are required";
    if (!form.priority_screen.trim())
      e.priority_screen = "Priority is required";
    if (!form.changefreq.trim()) e.changefreq = "Change Freq is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const setField = (key, val) => {
    setForm((prev) => ({ ...prev, [key]: val }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: null }));
  };

  // ── Pagination & search ───────────────────────────────────────────────────
  const filtered = seoList.filter((s) =>
    [s.title_name, s.description, s.keywords, s.call_function_name]
      .join(" ")
      .toLowerCase()
      .includes(search.toLowerCase()),
  );
  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const paginated = filtered.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  // ─────────────────────────────────────────────────────────────────────────
  // LIST VIEW
  // ─────────────────────────────────────────────────────────────────────────
  if (!formVisible) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-bold text-foreground">SEO Manager</h2>
            <p className="text-sm text-muted-foreground">
              Manage meta titles, descriptions and keywords for each page.
            </p>
          </div>
          <Button
            className="gap-2 rounded-xl self-start"
            onClick={() => {
              resetForm();
              setFormVisible(true);
            }}
          >
            <Plus className="h-4 w-4" /> Add SEO
          </Button>
        </div>

        {/* Search */}
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search by title, description, keywords..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
            className="pl-9"
          />
        </div>

        {/* Table */}
        <Card>
          {/* Loading */}
          {fetchLoader && (
            <CardContent className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </CardContent>
          )}

          {/* Empty */}
          {!fetchLoader && paginated.length === 0 && (
            <CardContent className="flex flex-col items-center justify-center py-16 text-center">
              <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-muted">
                <Globe className="h-6 w-6 text-muted-foreground" />
              </div>
              <p className="text-sm font-semibold text-foreground">
                No SEO entries found
              </p>
              <p className="mb-4 text-xs text-muted-foreground">
                {search
                  ? "Try a different search term."
                  : "Add your first SEO entry to get started."}
              </p>
              {!search && (
                <Button
                  size="sm"
                  className="gap-2 rounded-xl"
                  onClick={() => {
                    resetForm();
                    setFormVisible(true);
                  }}
                >
                  <Plus className="h-4 w-4" /> Add SEO
                </Button>
              )}
            </CardContent>
          )}

          {/* Table rows */}
          {!fetchLoader && paginated.length > 0 && (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap">
                        Create Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Title
                      </th>
                      <th className="hidden px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground md:table-cell">
                        Description
                      </th>
                      <th className="hidden px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground lg:table-cell">
                        Keywords
                      </th>
                      <th className="hidden px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground sm:table-cell">
                        Priority
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginated.map((seo) => (
                      <SeoTableRow
                        key={seo.primary_id}
                        seo={seo}
                        onEdit={handleEdit}
                        onDelete={(s) => setDeleteTarget(s)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between border-t border-border px-6 py-4">
                <p className="text-xs text-muted-foreground">
                  Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                  {Math.min(currentPage * itemsPerPage, filtered.length)} of{" "}
                  {filtered.length} entries
                </p>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="icon-sm"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((p) => p - 1)}
                  >
                    <ChevronLeft className="h-3.5 w-3.5" />
                  </Button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(
                      (p) =>
                        p === 1 ||
                        p === totalPages ||
                        Math.abs(p - currentPage) <= 1,
                    )
                    .map((p, i, arr) => (
                      <span key={p} className="flex items-center gap-1">
                        {i > 0 && arr[i - 1] !== p - 1 && (
                          <span className="px-1 text-xs text-muted-foreground">
                            …
                          </span>
                        )}
                        <button
                          onClick={() => setCurrentPage(p)}
                          className={`flex h-7 w-7 items-center justify-center rounded-lg text-xs font-medium transition-all ${
                            currentPage === p
                              ? "bg-gradient-primary text-white shadow-glow"
                              : "border border-border text-muted-foreground hover:border-primary/30 hover:text-primary"
                          }`}
                        >
                          {p}
                        </button>
                      </span>
                    ))}
                  <Button
                    variant="outline"
                    size="icon-sm"
                    disabled={currentPage === totalPages || totalPages === 0}
                    onClick={() => setCurrentPage((p) => p + 1)}
                  >
                    <ChevronRight className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </Card>

        {!fetchLoader && (
          <p className="text-xs text-muted-foreground">
            Showing {filtered.length} of {seoList.length} SEO entries
          </p>
        )}

        {/* Delete Modal */}
        {deleteTarget && (
          <DeleteModal
            seo={deleteTarget}
            onConfirm={() =>
              master_data_action_update(deleteTarget.primary_id, "d")
            }
            onClose={() => setDeleteTarget(null)}
            deleteLoader={deleteLoader}
          />
        )}
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // FORM VIEW
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-foreground">
            {editingId !== "0" ? "Edit SEO Entry" : "Add SEO"}
          </h2>
          <p className="text-sm text-muted-foreground">
            {editingId !== "0"
              ? "Update the SEO details below."
              : "Add meta information for a page."}
          </p>
        </div>
        <Button
          variant="outline"
          className="gap-2 rounded-xl"
          onClick={() => {
            resetForm();
            setFormVisible(false);
          }}
        >
          <ArrowLeft className="h-4 w-4" /> Back to List
        </Button>
      </div>

      {/* Error banner */}
      {Object.values(errors).some(Boolean) && (
        <div className="flex items-start gap-3 rounded-2xl border border-destructive/20 bg-destructive/5 p-4">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
          <p className="text-sm font-semibold text-destructive">
            Please fill in all required fields before saving.
          </p>
        </div>
      )}

      {/* Page Title */}
      <Card>
        <div className="border-b border-border px-6 py-4">
          <h3 className="text-sm font-bold text-foreground">Page Title</h3>
          <p className="text-xs text-muted-foreground">
            The title shown in browser tabs and search results
          </p>
        </div>
        <CardContent className="p-6">
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-foreground">
              Title <span className="text-destructive">*</span>
            </label>
            <Input
              id="title_name"
              name="title_name"
              placeholder="Enter Title*"
              value={form.title_name}
              onChange={(e) => setField("title_name", e.target.value)}
              className={errors.title_name ? "border-destructive" : ""}
            />
            {errors.title_name && (
              <p className="mt-1 text-xs text-destructive">
                {errors.title_name}
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Page Links */}
      <Card>
        <div className="border-b border-border px-6 py-4">
          <h3 className="text-sm font-bold text-foreground">Page Links</h3>
          <p className="text-xs text-muted-foreground">
            Actual and pretty URL paths for this page
          </p>
        </div>
        <CardContent className="p-6">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-foreground">
                Actual Link
              </label>
              <Input
                id="call_function_name"
                name="call_function_name"
                placeholder="Enter Actual Link*"
                value={form.call_function_name}
                onChange={(e) => setField("call_function_name", e.target.value)}
              />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-foreground">
                Pretty Link
              </label>
              <Input
                id="pretty_function_name"
                name="pretty_function_name"
                placeholder="Enter Pretty Link*"
                value={form.pretty_function_name}
                onChange={(e) =>
                  setField("pretty_function_name", e.target.value)
                }
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Image Links */}
      <Card>
        <div className="border-b border-border px-6 py-4">
          <h3 className="text-sm font-bold text-foreground">Image Links</h3>
          <p className="text-xs text-muted-foreground">
            Favicon and Open Graph image URLs
          </p>
        </div>
        <CardContent className="p-6">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-foreground">
                Favicon Images Link <span className="text-destructive">*</span>
              </label>
              <Input
                id="favicon"
                name="favicon"
                placeholder="Enter Favicon Images Link*"
                value={form.favicon}
                onChange={(e) => setField("favicon", e.target.value)}
                className={errors.favicon ? "border-destructive" : ""}
              />
              {errors.favicon && (
                <p className="mt-1 text-xs text-destructive">
                  {errors.favicon}
                </p>
              )}
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-foreground">
                Page Image Link <span className="text-destructive">*</span>
              </label>
              <Input
                id="image_link"
                name="image_link"
                placeholder="Enter Page Image Link*"
                value={form.image_link}
                onChange={(e) => setField("image_link", e.target.value)}
                className={errors.image_link ? "border-destructive" : ""}
              />
              {errors.image_link && (
                <p className="mt-1 text-xs text-destructive">
                  {errors.image_link}
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Description */}
      <Card>
        <div className="border-b border-border px-6 py-4">
          <h3 className="text-sm font-bold text-foreground">Description</h3>
          <p className="text-xs text-muted-foreground">
            Meta description shown in search engine results
          </p>
        </div>
        <CardContent className="p-6">
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-foreground">
              Description <span className="text-destructive">*</span>
            </label>
            <Textarea
              id="description"
              name="description"
              placeholder="Enter Description*"
              rows={4}
              minLength={3}
              value={form.description}
              onChange={(e) => setField("description", e.target.value)}
              className={errors.description ? "border-destructive" : ""}
            />
            {errors.description && (
              <p className="mt-1 text-xs text-destructive">
                {errors.description}
              </p>
            )}
            <p className="mt-1 text-[11px] text-muted-foreground">
              {form.description.length} characters
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Keywords */}
      <Card>
        <div className="border-b border-border px-6 py-4">
          <h3 className="text-sm font-bold text-foreground">Keywords</h3>
          <p className="text-xs text-muted-foreground">
            Comma-separated keywords for search engines
          </p>
        </div>
        <CardContent className="p-6">
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-foreground">
              Keywords <span className="text-destructive">*</span>
            </label>
            <Textarea
              id="keywords"
              name="keywords"
              placeholder="Enter Keyword*"
              rows={3}
              minLength={3}
              maxLength={300}
              value={form.keywords}
              onChange={(e) => setField("keywords", e.target.value)}
              className={errors.keywords ? "border-destructive" : ""}
            />
            {errors.keywords && (
              <p className="mt-1 text-xs text-destructive">{errors.keywords}</p>
            )}
            <p className="mt-1 text-[11px] text-muted-foreground">
              {form.keywords.length} / 300 characters
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Sitemap Settings */}
      <Card>
        <div className="border-b border-border px-6 py-4">
          <h3 className="text-sm font-bold text-foreground">
            Sitemap Settings
          </h3>
          <p className="text-xs text-muted-foreground">
            Priority and change frequency for the XML sitemap
          </p>
        </div>
        <CardContent className="p-6">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-foreground">
                Priority <span className="text-destructive">*</span>
              </label>
              <Input
                id="priority_screen"
                name="priority_screen"
                placeholder="Enter Priority* e.g. 0.8"
                value={form.priority_screen}
                onChange={(e) => setField("priority_screen", e.target.value)}
                className={errors.priority_screen ? "border-destructive" : ""}
              />
              {errors.priority_screen && (
                <p className="mt-1 text-xs text-destructive">
                  {errors.priority_screen}
                </p>
              )}
              <p className="mt-1 text-[11px] text-muted-foreground">
                Value between 0.0 and 1.0 (e.g. 1.0, 0.9, 0.8)
              </p>
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-foreground">
                Change Freq <span className="text-destructive">*</span>
              </label>
              <div className="relative">
                <select
                  id="changefreq"
                  name="changefreq"
                  value={form.changefreq}
                  onChange={(e) => setField("changefreq", e.target.value)}
                  className={`flex h-9 w-full appearance-none rounded-lg border bg-background px-3 py-1 pr-8 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${errors.changefreq ? "border-destructive" : "border-input"}`}
                >
                  <option value="always">Always</option>
                  <option value="hourly">Hourly</option>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="yearly">Yearly</option>
                  <option value="never">Never</option>
                </select>
                <ChevronRight className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 rotate-90 text-muted-foreground" />
              </div>
              {errors.changefreq && (
                <p className="mt-1 text-xs text-destructive">
                  {errors.changefreq}
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Save / Cancel */}
      <div className="flex justify-end gap-3 border-t border-border pt-4">
        <Button
          variant="outline"
          className="rounded-xl"
          onClick={() => {
            resetForm();
            setFormVisible(false);
          }}
        >
          Cancel
        </Button>
        <Button
          className="gap-2 rounded-xl"
          disabled={saveLoader}
          onClick={handleSaveChangesdynamic}
        >
          {saveLoader ? (
            <span className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Saving...
            </span>
          ) : (
            <>
              <Save className="h-4 w-4" />{" "}
              {editingId !== "0" ? "Save Changes" : "Save"}
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default SeoManager;
